import { useState, useEffect, useRef, useCallback } from 'react';
import { getVisitsForRange, aggregateVisitMetrics } from '@healthdesk/shared-services';

/**
 * Hook that fetches visit data for a clinic and returns aggregated metrics.
 *
 * @param {string} clinicId - Firestore clinic/facility ID
 * @param {number} rangeDays - Number of days to look back (7, 30, 90)
 * @returns {{ todayStats: Object|null, trendData: Object|null, loading: boolean, error: string|null }}
 */
export default function useClinicAnalytics(clinicId, rangeDays = 30) {
  const [todayStats, setTodayStats] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cacheRef = useRef({});

  const fetchData = useCallback(async () => {
    if (!clinicId) {
      setLoading(false);
      return;
    }

    const cacheKey = `${clinicId}_${rangeDays}`;
    if (cacheRef.current[cacheKey]) {
      const cached = cacheRef.current[cacheKey];
      setTodayStats(cached.todayStats);
      setTrendData(cached.trendData);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const endDate = new Date();
      endDate.setHours(23, 59, 59, 999);

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - rangeDays);
      startDate.setHours(0, 0, 0, 0);

      const visits = await getVisitsForRange(clinicId, startDate, endDate);
      const metrics = aggregateVisitMetrics(visits, startDate, endDate);

      cacheRef.current[cacheKey] = metrics;
      setTodayStats(metrics.todayStats);
      setTrendData(metrics.trendData);
    } catch (err) {
      console.error('[useClinicAnalytics]', err);
      setError('Failed to load analytics data.');
    } finally {
      setLoading(false);
    }
  }, [clinicId, rangeDays]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { todayStats, trendData, loading, error };
}
